import { useMemo } from 'react';

import { useTheme } from '@/components/common';

export type DesignTokens = {
  colors: {
    primary: string;
    surface: string;
    border: string;
    text: string;
    mutedText: string;
    error: string;
  };
  spacing: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
  };
  sizes: {
    inputHeight: number;
  };
  typography: {
    fontFamily: {
      regular: string;
    };
    fontSize: {
      sm: number;
      md: number;
      lg: number;
    };
  };
};

export function useDesignTokens(): DesignTokens {
  const theme = useTheme();

  return useMemo(() => {
    const regularFont =
      (theme as any)?.fonts?.bodyMedium?.fontFamily ||
      (theme as any)?.fonts?.regular?.fontFamily ||
      'System';

    const border =
      (theme as any)?.colors?.outline ||
      (theme as any)?.colors?.outlineVariant ||
      (theme as any)?.colors?.backdrop ||
      '#00000033';

    return {
      colors: {
        primary: theme.colors.primary,
        surface: theme.colors.surface,
        border,
        text: theme.colors.onSurface,
        mutedText: theme.colors.onSurfaceVariant,
        error: theme.colors.error,
      },
      spacing: {
        xs: 4,
        sm: 8,
        md: 12,
        lg: 16,
        xl: 24,
      },
      sizes: {
        inputHeight: 48,
      },
      typography: {
        fontFamily: {
          regular: regularFont,
        },
        fontSize: {
          sm: 12,
          md: 14,
          lg: 16,
        },
      },
    };
  }, [theme]);
}

// Convenience helper used across components.
export function useThemedStyles<T>(factory: (tokens: DesignTokens) => T): T {
  const tokens = useDesignTokens();
  return useMemo(() => factory(tokens), [tokens, factory]);
}
